import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, EmptyState } from "../_shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { MapPin, Plus, FileText, Pencil, Trash2, CheckCircle2, Layers, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const emptyForm = {
  name: "", address: "", material: "", avg_load_m3: "",
  placed_factor_low: "0.85", placed_factor_high: "0.95", status: "active", notes: "",
};

function TipsiteDialog({ open, onOpenChange, onSaved, editTipsite }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setForm(editTipsite ? {
        name: editTipsite.name || "", address: editTipsite.address || "", material: editTipsite.material || "",
        avg_load_m3: String(editTipsite.avg_load_m3 ?? ""),
        placed_factor_low: String(editTipsite.placed_factor_low ?? "0.85"),
        placed_factor_high: String(editTipsite.placed_factor_high ?? "0.95"),
        status: editTipsite.status || "active", notes: editTipsite.notes || "",
      } : emptyForm);
    }
  }, [open, editTipsite]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const avg = parseFloat(form.avg_load_m3);
    if (!form.name.trim()) { toast.error("Tipsite name is required"); return; }
    if (!(avg > 0)) { toast.error("Enter an average m³ per load (greater than 0)"); return; }
    const payload = {
      name: form.name.trim(),
      address: form.address.trim() || null,
      material: form.material.trim() || null,
      avg_load_m3: avg,
      placed_factor_low: parseFloat(form.placed_factor_low) || 0.85,
      placed_factor_high: parseFloat(form.placed_factor_high) || 0.95,
      notes: form.notes.trim() || null,
    };
    setSaving(true);
    try {
      if (editTipsite) await api.patch(`/landfill/tipsites/${editTipsite.id}`, { ...payload, status: form.status });
      else await api.post("/landfill/tipsites", payload);
      toast.success(editTipsite ? "Tipsite updated" : "Tipsite added");
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save tipsite"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="tipsite-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5 text-[#FF5F15]" />{editTipsite ? "Edit tipsite" : "Add tipsite"}</DialogTitle>
          <DialogDescription>A tipsite is a place loads are tipped. Volume is estimated from an average m³ per load.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={set("name")} placeholder="e.g. Trigg Road Cleanfill" data-testid="tipsite-name-input" className="mt-1" />
          </div>
          <div>
            <Label>Location / address</Label>
            <Input value={form.address} onChange={set("address")} placeholder="193 Trigg Road, Huapai, Auckland" data-testid="tipsite-address-input" className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Material</Label>
              <Input value={form.material} onChange={set("material")} placeholder="Clay / Cleanfill" data-testid="tipsite-material-input" className="mt-1" />
            </div>
            <div>
              <Label>Average m³ per load</Label>
              <Input type="number" step="0.1" min="0" value={form.avg_load_m3} onChange={set("avg_load_m3")} placeholder="5.5" data-testid="tipsite-avg-input" className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Placed factor — low</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.placed_factor_low} onChange={set("placed_factor_low")} data-testid="tipsite-factor-low-input" className="mt-1" />
            </div>
            <div>
              <Label>Placed factor — high</Label>
              <Input type="number" step="0.01" min="0" max="1" value={form.placed_factor_high} onChange={set("placed_factor_high")} data-testid="tipsite-factor-high-input" className="mt-1" />
            </div>
          </div>
          <p className="text-xs text-zinc-500">Placed volume = loose tipped volume × this range (allows for spreading &amp; compaction). Default 85–95%.</p>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={set("notes")} data-testid="tipsite-notes-input" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="tipsite-save-btn">{saving ? "Saving…" : editTipsite ? "Save changes" : "Add tipsite"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReportDialog({ open, onOpenChange, tipsite }) {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setStart(""); setEnd(""); } }, [open]);
  const generate = async () => {
    setBusy(true);
    try {
      const params = {};
      if (start) params.start = start;
      if (end) params.end = end;
      const r = await api.get(`/landfill/tipsites/${tipsite.id}/report.pdf`, { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      onOpenChange(false);
    } catch { toast.error("Could not generate report"); }
    finally { setBusy(false); }
  };
  if (!tipsite) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="tipsite-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-[#FF5F15]" />Volume report</DialogTitle>
          <DialogDescription>{tipsite.name} — council-ready truckloads &amp; estimated m³. Leave dates blank for all loads.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="tipsite-report-start" className="mt-1" />
          </div>
          <div>
            <Label>To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="tipsite-report-end" className="mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={generate} disabled={busy} data-testid="tipsite-report-generate"><Download className="mr-2 h-4 w-4" />{busy ? "Generating…" : "Generate PDF"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LandfillTipsites() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const [data, setData] = useState({ tipsites: [], active_tipsite_id: null, unassigned_loads: 0 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editTipsite, setEditTipsite] = useState(null);
  const [reportFor, setReportFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/landfill/tipsites").then((r) => setData(r.data || { tipsites: [] }))
      .catch(() => setData({ tipsites: [], active_tipsite_id: null, unassigned_loads: 0 }))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const activate = async (id) => {
    try { await api.post(`/landfill/tipsites/${id}/activate`); toast.success("Set as active intake tipsite"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not activate"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this tipsite?")) return;
    try { await api.delete(`/landfill/tipsites/${id}`); toast.success("Tipsite deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };
  const assignAllUnassigned = async (id) => {
    if (!window.confirm(`Assign all ${data.unassigned_loads} unassigned load(s) to this tipsite?`)) return;
    try { const r = await api.post(`/landfill/tipsites/${id}/assign-loads`, { all_unassigned: true }); toast.success(`Assigned ${r.data.assigned} load(s)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not assign"); }
  };

  return (
    <div className="space-y-5" data-testid="landfill-tipsites">
      <PageHeader tag="Landfill Operations" title="Tipsites" subtitle="Where loads are tipped — track truckloads and estimated volume for council reporting."
        action={<Button onClick={() => { setEditTipsite(null); setDialog(true); }} data-testid="tipsite-add-btn"><Plus className="mr-2 h-4 w-4" />Add tipsite</Button>} />

      {!loading && data.unassigned_loads > 0 && data.active_tipsite_id && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="tipsite-unassigned-banner">
          <span className="text-sm text-amber-800">{data.unassigned_loads} load{data.unassigned_loads === 1 ? "" : "s"} not yet assigned to a tipsite.</span>
          <Button size="sm" variant="outline" onClick={() => assignAllUnassigned(data.active_tipsite_id)} data-testid="tipsite-assign-all-btn">
            <Layers className="mr-2 h-4 w-4" />Assign all to active tipsite
          </Button>
        </div>
      )}

      {loading ? <div className="p-6 text-sm text-zinc-500">Loading…</div>
        : !data.tipsites.length ? <EmptyState icon={MapPin} title="No tipsites yet" description="Add a tipsite, then new loads attach to it automatically and you can generate volume reports." />
        : <div className="grid gap-4 md:grid-cols-2">
            {data.tipsites.map((t) => (
              <div key={t.id} className="rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-700 dark:bg-zinc-900" data-testid={`tipsite-card-${t.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">{t.name}</h3>
                      {t.is_active && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`tipsite-active-badge-${t.id}`}>Active intake</Badge>}
                      {t.status === "closed" && <Badge variant="outline" className="text-zinc-500">Closed</Badge>}
                    </div>
                    {t.address && <p className="mt-0.5 text-sm text-zinc-500">{t.address}</p>}
                    <p className="mt-0.5 text-xs text-zinc-400">{t.material || "Cleanfill"} · {t.avg_load_m3} m³ avg/load</p>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800"><div className="text-lg font-bold" data-testid={`tipsite-loadcount-${t.id}`}>{t.load_count}</div><div className="text-[10px] uppercase text-zinc-500">Loads</div></div>
                  <div className="rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800"><div className="text-lg font-bold" data-testid={`tipsite-loose-${t.id}`}>{t.loose_m3}</div><div className="text-[10px] uppercase text-zinc-500">Loose m³</div></div>
                  <div className="rounded-lg bg-[#FFF2EC] p-2"><div className="text-lg font-bold text-[#FF5F15]">{t.placed_central_m3}</div><div className="text-[10px] uppercase text-zinc-500">Placed m³</div></div>
                </div>
                <p className="mt-1 text-center text-[11px] text-zinc-400">Placed range {t.placed_low_m3}–{t.placed_high_m3} m³</p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => setReportFor(t)} data-testid={`tipsite-report-btn-${t.id}`}><FileText className="mr-2 h-4 w-4" />Report</Button>
                  {!t.is_active && <Button size="sm" variant="outline" onClick={() => activate(t.id)} data-testid={`tipsite-activate-btn-${t.id}`}><CheckCircle2 className="mr-2 h-4 w-4" />Set active</Button>}
                  <Button size="sm" variant="ghost" onClick={() => { setEditTipsite(t); setDialog(true); }} data-testid={`tipsite-edit-btn-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  {isAdmin && <Button size="sm" variant="ghost" onClick={() => remove(t.id)} data-testid={`tipsite-delete-btn-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                </div>
              </div>
            ))}
          </div>}

      <TipsiteDialog open={dialog} onOpenChange={setDialog} onSaved={load} editTipsite={editTipsite} />
      <ReportDialog open={!!reportFor} onOpenChange={(v) => !v && setReportFor(null)} tipsite={reportFor} />
    </div>
  );
}
